<template>
    <div>
        <Navbar />
        <div class="disclaimer-page">
            <section class="disclaimer-hero">
                <div class="disclaimer-hero-shell">
                    <p class="disclaimer-kicker">Legal &amp; Disclosures</p>
                    <h1>Full Disclaimer</h1>
                    <p class="disclaimer-subtitle">Please read this disclaimer carefully before relying on any information presented on this website.</p>
                </div>
            </section>

            <section class="disclaimer-body-section">
                <div class="disclaimer-shell">

                    <article class="disclaimer-block">
                        <h2>1. About This Disclaimer</h2>
                        <p>This disclaimer applies to the Norland Advisory website, including all pages, articles, blog posts, guides, portfolio data, suburb statistics, interactive tools, maps, videos, downloads, and forms (together, the &ldquo;Website&rdquo;). The Website is operated by Norland
                            Advisory Pty Ltd (ACN/ABN details available on request) from Melbourne, Victoria, Australia. By accessing the Website you acknowledge and accept this disclaimer.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>2. General Information Only &mdash; Not Advice</h2>
                        <p>Norland Advisory is a licensed real-estate company. We are not a licensed financial adviser, Australian Financial Services Licence (AFSL) holder, authorised representative, tax agent, mortgage broker, credit provider, lawyer, quantity surveyor, or town planner, and we do
                            not hold ourselves out as any of those things.</p>
                        <p>Nothing on the Website is:</p>
                        <ul>
                            <li>personal financial product advice within the meaning of the <em>Corporations Act 2001</em> (Cth);</li>
                            <li>tax advice within the meaning of the <em>Tax Agent Services Act 2009</em> (Cth);</li>
                            <li>credit advice within the meaning of the <em>National Consumer Credit Protection Act 2009</em> (Cth);</li>
                            <li>legal advice;</li>
                            <li>a valuation, building report, pest report, depreciation schedule, or engineering report;</li>
                            <li>an offer or invitation to buy or sell any financial product, security, managed investment scheme, or real estate.</li>
                        </ul>
                        <p>All content is general in nature and has been prepared without taking into account your personal objectives, financial situation, or needs. Before acting on anything you read here, you should consider its appropriateness having regard to your own circumstances and obtain
                            independent professional advice from suitably licensed advisers.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>3. Past Performance Is Not a Reliable Indicator of Future Results</h2>
                        <p>The Norland portfolio page and related marketing materials publish historical real-estate transaction data: purchase prices, weekly rents, rental yields, capital gains, annual growth percentages, &ldquo;valuations&rdquo;, valuation dates, and similar figures. These
                            figures:</p>
                        <ul>
                            <li>are specific to individual properties, clients, suburbs, and points in time;</li>
                            <li>reflect the market, finance, tax, and regulatory conditions prevailing at the time of each transaction;</li>
                            <li>are not forecasts, not guarantees, and are not indicative of how any future investment will perform;</li>
                            <li>may include internal estimates of &ldquo;valuation&rdquo; that have not been independently valued by a licensed valuer;</li>
                            <li>exclude many real holding costs &mdash; including loan interest, land tax, council rates, insurance, depreciation, maintenance, vacancy, management fees, and personal tax &mdash; which will materially affect the real return to any individual investor.</li>
                        </ul>
                        <p>Property prices can fall as well as rise. Interest rates, rental demand, tenant behaviour, planning rules, zoning, taxation, and finance availability all change. You should not rely on past performance to assess any future property decision.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>4. Testimonials and Case Studies</h2>
                        <p>Client testimonials published on the Website are provided by real Norland Advisory clients about their own experience with a specific property. They:</p>
                        <ul>
                            <li>reflect the individual experience of the particular client at a particular time;</li>
                            <li>are not endorsements of any specific investment strategy, property type, or suburb;</li>
                            <li>are not a representation that any similar result is available to you;</li>
                            <li>are not typical &mdash; individual client outcomes vary widely.</li>
                        </ul>
                        <p>Financial figures quoted within testimonials are provided by the client based on their own records and represent historical outcomes only.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>5. Worked Examples, Guides and Blog Articles</h2>
                        <p>Yields, returns, build costs, rents, ROI percentages, payback periods, refinance outcomes, &ldquo;before / after&rdquo; comparisons, and similar figures shown in the guides and blog articles are illustrative examples based on past transactions or standard scenarios. They
                            are not projections of what any particular property will achieve for any particular investor. Actual outcomes depend on purchase price, finance structure, interest rate, renovation cost, vacancy, maintenance, rates, land tax, insurance, depreciation, personal tax
                            position, and market movements &mdash; none of which are guaranteed by Norland Advisory.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>6. Third-Party Data, Suburb Statistics, and Interactive Maps</h2>
                        <p>The Market Insights and Suburb Stats tools display data sourced from government and industry bodies, including (without limitation): Valuer-General Victoria, Homes Victoria, the Australian Bureau of Statistics, the Australian Taxation Office, Crime Statistics Agency
                            Victoria, the Victorian Building Authority, the Clean Energy Regulator, Victorian Planning Provisions, the State Emergency Service, Country Fire Authority, and OpenStreetMap. This data:</p>
                        <ul>
                            <li>may be aggregated, delayed, estimated, sampled, or subject to revision by its original source;</li>
                            <li>may be incomplete or contain errors introduced during collection, processing, or presentation;</li>
                            <li>is provided &ldquo;as is&rdquo; for general research purposes only;</li>
                            <li>must not be relied on for any property purchase, valuation, lending, insurance, town-planning, safety, or legal decision.</li>
                        </ul>
                        <p>Planning overlays and hazard layers (bushfire, flood, powerlines, heritage, environmental significance) are informational only and are not a substitute for a Section 32 vendor&rsquo;s statement, a legal property search, a building surveyor&rsquo;s inspection, or
                            professional advice. Always confirm data with the original source and a suitably qualified professional before acting.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>7. No Reliance; Limitation of Liability</h2>
                        <p>To the maximum extent permitted by law, Norland Advisory, its directors, employees, contractors, and agents:</p>
                        <ul>
                            <li>make no representation or warranty, express or implied, about the accuracy, completeness, currency, reliability, suitability, or availability of any information on the Website;</li>
                            <li>accept no liability for any loss or damage (including indirect, consequential, or economic loss) suffered by any person in connection with their use of, or reliance on, the Website or its content;</li>
                            <li>accept no liability for any error, omission, interruption, delay, virus, or unauthorised access affecting the Website.</li>
                        </ul>
                        <p>Nothing in this disclaimer excludes, restricts, or modifies any consumer guarantee, right, or remedy conferred on you by the Australian Consumer Law (Schedule 2 to the <em>Competition and Consumer Act 2010</em> (Cth)) or any other legislation where to do so would be
                            unlawful.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>8. External Links</h2>
                        <p>The Website may link to third-party websites and resources. Norland Advisory does not control, endorse, or accept responsibility for the content of those sites, and provides the links for convenience only.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>9. Forward-Looking Statements</h2>
                        <p>Any statement about future events &mdash; including about the Melbourne or Victorian property market, interest rates, rental demand, capital growth, planning rules, tax treatment, or construction costs &mdash; is a forward-looking statement that involves known and unknown
                            risks and uncertainties. Actual events may differ materially from what is described. Norland Advisory is under no obligation to update any forward-looking statement after it is published.</p>
                    </article>

                    <article class="disclaimer-block">
                        <h2>10. Contact</h2>
                        <p>Questions about this disclaimer can be sent to <a href="mailto:info@norlandproperty.com.au">info@norlandproperty.com.au</a> or by post to 602, 21 Ellingworth Parade, Box Hill, VIC 3128.</p>
                    </article>

                    <p class="disclaimer-updated">Last updated: May 2026</p>
                </div>
            </section>
        </div>
        <SiteFooter />
    </div>
</template>

<script>
import Navbar from '@/components/layout/Navbar.vue'
import SiteFooter from '@/components/layout/SiteFooter.vue'

export default {
    components: { Navbar, SiteFooter }
}
</script>

<style scoped>
.disclaimer-page {
    padding-top: 118px;
    --c-primary: #2f7a6c;
    --c-primary-deep: #1e5f53;
    --c-accent-soft: #b8dcd4;
    --c-text-strong: #2f4c45;
    --c-text-body: #5e7972;
    --c-text-muted: #7b918b;
    --c-card-border: #d2e3de;
    --c-bg-warm: #eef6f3;
    --c-on-dark: #edf8f5;
}

/* Hero */
.disclaimer-hero {
    padding: 56px 6% 52px;
    background:
        radial-gradient(circle at 10% 12%, rgba(31, 107, 79, 0.28), transparent 36%),
        radial-gradient(circle at 88% 82%, rgba(203, 135, 69, 0.18), transparent 30%),
        linear-gradient(120deg, #0b4f47 0%, #0a2c22 52%, #136456 100%);
    color: var(--c-on-dark);
    text-align: center;
}

.disclaimer-hero-shell {
    max-width: 800px;
    margin: 0 auto;
    display: grid;
    gap: 12px;
}

.disclaimer-kicker {
    margin: 0;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--c-accent-soft);
}

.disclaimer-hero h1 {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 700;
    color: #f2fcf8;
    line-height: 1.1;
}

.disclaimer-subtitle {
    margin: 0;
    font-size: clamp(0.95rem, 1.6vw, 1.1rem);
    color: rgba(237, 248, 245, 0.82);
    line-height: 1.6;
}

/* Body */
.disclaimer-body-section {
    padding: 64px 6% 80px;
    background:
        radial-gradient(circle at 6% 8%, rgba(26, 95, 70, 0.05), transparent 34%),
        #f5f9f7;
}

.disclaimer-shell {
    max-width: 860px;
    margin: 0 auto;
    display: grid;
    gap: 36px;
}

.disclaimer-block {
    border-left: 3px solid var(--c-primary);
    padding-left: 20px;
}

.disclaimer-block h2 {
    margin: 0 0 10px;
    font-size: clamp(1rem, 1.6vw, 1.2rem);
    font-weight: 700;
    color: var(--c-primary-deep);
    line-height: 1.25;
}

.disclaimer-block p {
    margin: 0;
    color: var(--c-text-body);
    line-height: 1.78;
    font-size: clamp(0.92rem, 1.1vw, 1rem);
}

.disclaimer-block p+p,
.disclaimer-block p+ul,
.disclaimer-block ul+p {
    margin-top: 10px;
}

.disclaimer-block ul {
    margin: 10px 0 0;
    padding-left: 1.4em;
    color: var(--c-text-body);
    line-height: 1.78;
    font-size: clamp(0.92rem, 1.1vw, 1rem);
}

.disclaimer-block ul li {
    margin-bottom: 4px;
}

.disclaimer-block a {
    color: var(--c-primary);
    text-decoration: underline;
}

.disclaimer-block a:hover {
    color: var(--c-primary-deep);
}

.disclaimer-updated {
    margin: 0;
    font-size: 0.82rem;
    color: var(--c-text-muted);
    text-align: right;
    padding-top: 16px;
    border-top: 1px solid var(--c-card-border);
}

@media (max-width: 768px) {
    .disclaimer-page {
        padding-top: 106px;
    }

    .disclaimer-hero {
        padding: 44px 4% 40px;
    }

    .disclaimer-body-section {
        padding: 48px 4% 60px;
    }
}
</style>
